import { PictoFacilitePaiement, PictoFormulaireEnLigne } from '@/assets/icons-april-fr/pictos';
import AprilPicto from '@/components/shared/april-picto';
import SmallSpinner from '@/components/shared/spinners/small-spinner';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { useContracts } from '@/hooks/api/use-contracts';
import { useReceiptsUnpaid } from '@/hooks/api/use-receipts';
import { useWaitingDocContracts } from '@/hooks/api/use-waiting-doc-contracts';
import type { Receipt } from '@/types/api/receipt';

/** Champs de quittance affichés par le bloc. */
type TodoReceipt = Receipt & {
  numeroPolice?: string;
  solde?: number | string;
  pay_link?: string | null;
  pay_link_reason?: string[];
};

const TodoBloc = () => {
  const receiptsQuery = useReceiptsUnpaid();
  const waitingQuery = useWaitingDocContracts();
  const contractsQuery = useContracts();

  /* Comme l'ancien loader : contenu masqué pendant chaque lecture (relancées à chaque montage). */
  const loading = [receiptsQuery, waitingQuery, contractsQuery].some((q) => q.isFetching);

  if (loading) return <SmallSpinner />;

  /* Corps de réponse complet `{ data, meta, links }` (ancien `state.receipts_unpaid.data`). */
  const receipts = receiptsQuery.data;
  const documentsWaiting = waitingQuery.data;

  // Conservé tel quel : l'ancien code testait `receipts.data.length`, `receipts.data` étant
  // l'enveloppe `{ data, meta, links }` — sans `length` —, ce critère ne s'applique jamais.
  const hasReceipts = ((receipts as { length?: number } | undefined)?.length ?? 0) > 1;
  const hasDocuments = (documentsWaiting?.length ?? 0) > 1 && documentsWaiting?.find((o) => o.upload_app_link);

  if (!hasReceipts && !hasDocuments) {
    return null;
  }

  return (
    <div className="mb-6">
      <h2 className="text-lg font-semibold mb-6">À faire</h2>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {receipts &&
          (receipts.data as TodoReceipt[]).map((r, i) => (
            <Card key={i} className="border-warning/30 bg-warning/5">
              <CardContent className="flex flex-col items-center text-center py-6">
                <div className="w-12 h-12 rounded-full bg-warning/20 flex items-center justify-center mb-6">
                  <AprilPicto icon={PictoFacilitePaiement} size="lg" />
                </div>
                <p className="text-sm font-semibold text-destructive mb-2">Vous avez une cotisation à régler !</p>
                <p className="text-sm text-muted-foreground italic mb-6">
                  Contrat : {r.numeroPolice} - {r.solde}€ restant
                </p>
                {r.pay_link ? (
                  <Button asChild variant="default">
                    <a target="_blank" href={`${r.pay_link}?url_return=${window.location.href}`} rel="noreferrer">
                      Payer ma cotisation
                    </a>
                  </Button>
                ) : (
                  <span className="inline-flex items-center rounded-md bg-blue-50 px-2 py-1 text-sm font-medium text-blue-700">
                    {(r.pay_link_reason?.[0] ?? '').replace('_', ' ')}
                  </span>
                )}
              </CardContent>
            </Card>
          ))}

        {documentsWaiting &&
          documentsWaiting.map((o, i) => (
            <Card key={i} className="border-warning/30 bg-warning/5">
              <CardContent className="flex flex-col items-center text-center py-6">
                <div className="w-12 h-12 rounded-full bg-warning/20 flex items-center justify-center mb-6">
                  <AprilPicto icon={PictoFormulaireEnLigne} size="lg" />
                </div>
                <p className="text-sm font-semibold text-destructive mb-2">Vous avez des documents à envoyer !</p>
                <p className="text-sm text-muted-foreground italic mb-6">Contrat : {o.numeroPolice}</p>
                <Button asChild variant="default">
                  <a target="_blank" href={o.upload_app_link} rel="noreferrer">
                    Envoyer mes documents
                  </a>
                </Button>
              </CardContent>
            </Card>
          ))}
      </div>
    </div>
  );
};

export default TodoBloc;
